import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/hooks/useApi';
import { useModeAwareMutation } from '@/hooks/useModeAwareQuery';
import { CUSTOMER_TIMELINE_QUERY_KEYS, invalidateCustomerTimeline } from '@/lib/note-query-keys';
import { useBulkDeleteNotes } from './useBulkDeleteNotes';

export interface UpdateCaseNoteRequest {
  caseId: string;
  noteId: string;
  content: string;
}

export interface DeleteCaseNoteRequest {
  caseId: string;
  noteId: string;
}

function useCaseNoteInvalidation() {
  const queryClient = useQueryClient();

  return (caseId: string) => {
    queryClient.invalidateQueries({ queryKey: ['case-notes', caseId] });
    queryClient.invalidateQueries({ queryKey: ['case-recent-activities'] });
    queryClient.invalidateQueries({ queryKey: ['case-audit-logs'] });
    invalidateCustomerTimeline(queryClient);
  };
}

export function useUpdateCaseNote() {
  const { apiPut } = useApi();
  const invalidate = useCaseNoteInvalidation();

  return useModeAwareMutation<void, Error, UpdateCaseNoteRequest>(
    async (request) => {
      await apiPut<unknown>(`/api/v1/collection-cases/${request.caseId}/notes/${request.noteId}`, {
        content: request.content,
      });
    },
    () => undefined,
    {
      onSuccess: (_, variables) => invalidate(variables.caseId),
    },
  );
}

export function useDeleteCaseNote() {
  const { apiDelete } = useApi();
  const invalidate = useCaseNoteInvalidation();

  return useModeAwareMutation<void, Error, DeleteCaseNoteRequest>(
    async (request) => {
      await apiDelete<void>(`/api/v1/collection-cases/${request.caseId}/notes/${request.noteId}`);
    },
    () => undefined,
    {
      onSuccess: (_, variables) => invalidate(variables.caseId),
    },
  );
}

export function useDeleteBulkCaseNotes() {
  return useBulkDeleteNotes('/api/v1/collection-cases/notes/bulk-delete', [
    'case-notes',
    'case-recent-activities',
    'case-audit-logs',
    ...CUSTOMER_TIMELINE_QUERY_KEYS,
  ]);
}
